import type { AiDebugPayload } from '@digital-cs/shared';

/**
 * 把 ai.debug 事件 payload 格式化输出到浏览器 console。
 *
 * 每个字段附「中文含义 + 备选值集」，便于不熟悉后端识别管线的开发同学
 * 直接在控制台看懂每一轮对话的判定结果。
 *
 * 输出形态：console.groupCollapsed（默认折叠）+ 字段逐行 log；
 * 一眼可读的 header 含轮次/意图/留资判定/分支摘要，展开看详情。
 */

const INTENT_LABEL_DESC: Record<string, string> = {
  cs_qa: '正常客服问答',
  complaint: '投诉',
  pricing: '询价/商务咨询',
  chitchat: '闲聊/问候',
  spam: '垃圾/试探注入',
  unclear: '无法判断',
};

const EMOTION_LABEL_DESC: Record<string, string> = {
  positive: '正面',
  neutral: '中性',
  negative: '负面',
  angry: '愤怒',
  frustrated: '沮丧',
};

const TICKET_CATEGORY_DESC: Record<string, string> = {
  complaint: '投诉',
  refund: '退款',
  escalation: '升级',
  other: '其他',
};

const TICKET_SEVERITY_DESC: Record<string, string> = {
  low: '低',
  medium: '中',
  high: '高',
};

const PREFIX = '[数智客服]';

export function printAiDebug(payload: AiDebugPayload): void {
  const leadHint =
    payload.leadEval?.shouldAsk === undefined
      ? 'n/a'
      : String(payload.leadEval.shouldAsk);
  const header = `${PREFIX} ai.debug · 第 ${payload.round} 轮 · intent=${payload.intent.intent} · lead.shouldAsk=${leadHint} · branch=${payload.reply.branch}`;

  /* eslint-disable no-console */
  console.groupCollapsed(header);

  // ── 上下文 ─────────────────────────────────────────────────
  console.log(
    '%c—— 上下文 ——',
    'color:#888;font-weight:bold',
  );
  console.log(
    'round =',
    payload.round,
    '· 访客已发消息轮次（用于留资 minRounds 门槛判断）',
  );
  console.log(
    'visitorText =',
    JSON.stringify(payload.visitorText),
    '· 访客本轮消息原文',
  );
  console.log('visitorMsgId =', payload.visitorMsgId, '· 消息行 id');

  // ── ① 意图 ────────────────────────────────────────────────
  console.log(
    '%c—— ① 意图分类 (IntentService) ——',
    'color:#3a8ee6;font-weight:bold',
  );
  const intentMeaning = INTENT_LABEL_DESC[payload.intent.intent] ?? '未知';
  console.log(
    'intent.intent =',
    payload.intent.intent,
    `· ${intentMeaning} · 备选: cs_qa(客服问答) / complaint(投诉) / pricing(询价) / chitchat(闲聊) / spam(垃圾) / unclear(不明)`,
  );
  console.log(
    'intent.inScope =',
    payload.intent.inScope,
    '· 是否在业务范围内 — false 时触发短路拒答；strict 策略下问候也会 false',
  );
  console.log(
    'intent.needsHuman =',
    payload.intent.needsHuman,
    '· 访客是否要求转人工 — true 表示模型识别出"想找真人客服"',
  );
  console.log(
    'intent.reason =',
    payload.intent.reason,
    '· 模型对本次判断的一句话理由（中文 / 任意自由文本）',
  );

  // ── ② 情绪 ────────────────────────────────────────────────
  if (payload.emotion) {
    console.log(
      '%c—— ② 情绪分析 (EmotionService) ——',
      'color:#67c23a;font-weight:bold',
    );
    const emoMeaning = EMOTION_LABEL_DESC[payload.emotion.label] ?? '未知';
    console.log(
      'emotion.label =',
      payload.emotion.label,
      `· ${emoMeaning} · 备选: positive(正面) / neutral(中性) / negative(负面) / angry(愤怒) / frustrated(沮丧)`,
    );
    console.log(
      'emotion.score =',
      payload.emotion.score.toFixed(2),
      '· 情绪强度 0~1，越接近 1 越显著；工单评估用 strategy.ticketPolicy.negativeEmotionThreshold (默认 0.7) 判断',
    );
    console.log(
      'emotion.reason =',
      payload.emotion.reason,
      '· 模型理由',
    );
  } else {
    console.log(
      '%c—— ② 情绪分析 ——  null',
      'color:#888',
      '· 短路 (out_of_scope) 分支不跑情绪分析',
    );
  }

  // ── ③ 留资 ────────────────────────────────────────────────
  if (payload.leadEval) {
    console.log(
      '%c—— ③ 留资评估 (LeadAssessmentService) ——',
      'color:#e6a23c;font-weight:bold',
    );
    console.log(
      'leadEval.shouldAsk =',
      payload.leadEval.shouldAsk,
      '· 是否应该弹留资卡问访客联系方式 — true 时会推 lead.card.show 事件',
    );
    console.log(
      'leadEval.score =',
      payload.leadEval.score,
      '· 留资意向评分 0~100 · 触发门槛 strategy.leadCriteria.minScore (默认 60)',
    );
    console.log(
      'leadEval.signals =',
      payload.leadEval.signals,
      '· 命中的留资信号（如「询问价格」「询问案例」「轮次>5」等）',
    );
    if (payload.leadEval.cooldownReason !== undefined) {
      console.log(
        'leadEval.cooldownReason =',
        payload.leadEval.cooldownReason,
        '· shouldAsk=false 的原因；常见值: "minScore 未达 60" / "minRounds 未到 5" / "跨会话冷却中 (24h)" / "本会话已问过"',
      );
    }
    if (payload.leadEval.askedFields !== undefined) {
      console.log(
        'leadEval.askedFields =',
        payload.leadEval.askedFields,
        '· 准备要问的字段子集 · 备选: name / phone / wechat / email',
      );
    }
  } else {
    console.log(
      '%c—— ③ 留资评估 ——  null',
      'color:#888',
      '· 短路分支不跑',
    );
  }

  // ── ④ 工单 ────────────────────────────────────────────────
  if (payload.ticketEval) {
    console.log(
      '%c—— ④ 工单评估 (TicketAssessmentService) ——',
      'color:#f56c6c;font-weight:bold',
    );
    console.log(
      'ticketEval.shouldCreate =',
      payload.ticketEval.shouldCreate,
      '· 是否要建工单 — 通常 intent=complaint 或负面情绪超阈值时为 true',
    );
    const cat = TICKET_CATEGORY_DESC[payload.ticketEval.category] ?? '未知';
    console.log(
      'ticketEval.category =',
      payload.ticketEval.category,
      `· ${cat} · 备选: complaint(投诉) / refund(退款) / escalation(升级) / other(其他)`,
    );
    const sev = TICKET_SEVERITY_DESC[payload.ticketEval.severity] ?? '未知';
    console.log(
      'ticketEval.severity =',
      payload.ticketEval.severity,
      `· ${sev} · 备选: low(低) / medium(中) / high(高)`,
    );
    console.log(
      'ticketEval.triggers =',
      payload.ticketEval.triggers,
      '· 命中的策略项（如 "intent=complaint"、"keyword:投诉"、"emotion=angry@0.9"）',
    );
  } else {
    console.log(
      '%c—— ④ 工单评估 ——  null',
      'color:#888',
      '· 短路分支不跑',
    );
  }

  // ── ⑤ 知识库 ──────────────────────────────────────────────
  console.log(
    '%c—— ⑤ 知识库 (KB) ——',
    'color:#909399;font-weight:bold',
  );
  console.log(
    'kb.hit =',
    payload.kb.hit,
    '· 启发式：kbItems 非空且本轮非兜底；记入 messages.kb_hit 字段',
  );
  console.log(
    'kb.itemCount =',
    payload.kb.itemCount,
    '· 拼 prompt 时取到的条目数（本地 KB 路径走通是 1 单 item / DB 路径是 N items；0 表示完全无 KB）',
  );

  // ── ⑥ 回答 ────────────────────────────────────────────────
  console.log(
    '%c—— ⑥ 主回答 ——',
    'color:#5c0011;font-weight:bold',
  );
  console.log(
    'reply.branch =',
    payload.reply.branch,
    '· main=走主模型流式回答 / out_of_scope=短路拒答模板（伪流式分帧推固定文案）',
  );
  console.log(
    'reply.isFallback =',
    payload.reply.isFallback,
    '· 是否走兜底文案 — true 表示：AI 网关挂 / 空回答 / 行为契约 #4 触发（KB 未覆盖）',
  );
  console.log(
    'reply.latencyMs =',
    payload.reply.latencyMs,
    '· 主流式 AI 调用耗时（不含三件评估）',
  );
  console.log(
    'reply.answerLen =',
    payload.reply.answerLen,
    '· AI 回答最终字符数',
  );

  // ── 总览 ──────────────────────────────────────────────────
  console.log(
    '%c—— 总览 ——',
    'color:#888;font-weight:bold',
  );
  console.log(
    'turnTotalMs =',
    payload.turnTotalMs,
    '· 本轮总耗时 ms（含意图分类 + 主回答 + Promise.all 三件评估 + 副作用 SQL）',
  );

  console.groupEnd();
  /* eslint-enable no-console */
}
